import axios from 'axios';
import { getActivePinia } from 'pinia';
import { APP_CONFIG, STORAGE_KEYS } from '@/config/app';
import { useLoadingStore } from '@/stores/loading';
import { i18n, t } from '@/i18n';
import { currentLocale } from '@/i18n';

const DEFAULT_TIMEOUT_MS = 15000;


// Mensajes por `code_string` de PAM: viven en i18n (`apiErrors.*`), así siguen el idioma activo.
const getErrorMessage = (payload, fallback) => {
  const codeString = payload?.code_string;
  if (codeString && i18n.global.te(`apiErrors.${codeString}`)) {
    return t(`apiErrors.${codeString}`);
  }
  if (payload && typeof payload.description === 'string' && payload.description.trim()) {
    return payload.description.trim();
  }
  if (payload && typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  return fallback;
};

const api = axios.create({
  baseURL: APP_CONFIG.apiBaseUrl,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

const getLoadingStore = () => {
  const pinia = getActivePinia();
  if (!pinia) return null;
  return useLoadingStore(pinia);
};

const resolveLoadingScope = (config) => {
  const url = String(config?.url || '').toLowerCase();
  if (url.includes('/wallet/')) return 'wallet';
  if (url.includes('/transactions')) return 'transactions';
  if (url.includes('/profile')) return 'profile';
  if (url.includes('/cms/')) return 'lobby';
  if (url.includes('/emara/')) return 'lobby';
  if (url.includes('/auth/')) return 'auth';
  return 'global';
};

const startLoadingForConfig = (config) => {
  const loadingStore = getLoadingStore();
  if (!loadingStore) return;
  const scope = resolveLoadingScope(config);
  loadingStore.start(scope);
  if (!config.__loadingMeta) {
    config.__loadingMeta = {};
  }
  config.__loadingMeta.scope = scope;
};

const finishLoadingForConfig = (config) => {
  const loadingStore = getLoadingStore();
  if (!loadingStore) return;
  const scope = config?.__loadingMeta?.scope || resolveLoadingScope(config);
  loadingStore.finish(scope);
};

const pamPath = (path) => {
  const normalized = String(path || '').trim();
  const suffix = normalized.startsWith('/') ? normalized : `/${normalized}`;
  return `${APP_CONFIG.pamRoutePrefix}${suffix}`;
};

const normalizeMessage = (payload, fallback) => {
  if (payload && typeof payload.description === 'string' && payload.description.trim()) {
    return payload.description.trim();
  }
  if (payload && typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  return fallback;
};

const buildError = ({ message, code = -1, codeString = '', status = 500, payload = null }) => {
  const error = new Error(message || t('apiFallback.requestFailed'));
  error.code = code;
  error.codeString = codeString;
  error.status = status;
  error.payload = payload;
  return error;
};

const normalizePamErrorValue = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  if (typeof value === 'boolean') {
    return value ? 1 : 0;
  }

  return null;
};

const handleAxiosError = (error, fallback) => {
  if (error && error.payload) {
    return error;
  }

  if (error?.response) {
    const payload = error.response.data;
    const message = getErrorMessage(payload, fallback);
    const code = Number(payload?.code);
    const codeString = payload?.code_string || '';
    return buildError({
      message,
      code: Number.isFinite(code) ? code : -1,
      codeString,
      status: Number(error.response.status || 500),
      payload,
    });
  }

  if (error?.request) {
    return buildError({
      message: t('apiFallback.noResponse', { action: fallback }),
      code: -1,
      codeString: 'NO_RESPONSE',
      status: 503,
      payload: null,
    });
  }

  return buildError({
    message: error?.message || fallback,
    code: -1,
    codeString: 'UNKNOWN_ERROR',
    status: 500,
    payload: null,
  });
};

const ensurePamEnvelope = (payload, fallbackMessage) => {
  if (!payload || typeof payload !== 'object') {
    throw buildError({
      message: t('apiFallback.invalidFormat', { action: fallbackMessage }),
      code: -1,
      codeString: 'INVALID_RESPONSE_FORMAT',
      status: 502,
      payload,
    });
  }

  const normalizedError = normalizePamErrorValue(payload.error);
  const normalizedCode = Number(payload.code);
  const hasCode = Number.isFinite(normalizedCode);
  if (!Number.isFinite(normalizedError) && !hasCode) {
    throw buildError({
      message: t('apiFallback.invalidFormat', { action: fallbackMessage }),
      code: -1,
      codeString: 'INVALID_RESPONSE_FORMAT',
      status: 502,
      payload,
    });
  }

  const hasError = (
    (Number.isFinite(normalizedError) && normalizedError !== 0)
    || (hasCode && normalizedCode !== 0)
  );
  if (hasError) {
    const message = getErrorMessage(payload, fallbackMessage);
    const errorCode = hasCode
      ? normalizedCode
      : (Number.isFinite(normalizedError) ? normalizedError : -1);
    throw buildError({
      message,
      code: errorCode,
      codeString: payload.code_string || '',
      status: 400,
      payload,
    });
  }

  return payload.data || {};
};

const toPublicError = (error, fallback = t('apiFallback.requestFailed')) => ({
  success: false,
  error: {
    message: error?.message || fallback,
    code: Number.isFinite(Number(error?.code)) ? Number(error.code) : -1,
    codeString: error?.codeString || '',
    status: Number.isFinite(Number(error?.status)) ? Number(error.status) : 500,
    payload: error?.payload || null,
  },
});

const inferTypeFromGame = ({ name, code }) => {
  const source = `${String(name || '')} ${String(code || '')}`.toLowerCase();
  if (source.includes('live')) return 'live_game';
  if (source.includes('roulette')) return 'roulette';
  if (source.includes('blackjack')) return 'blackjack';
  if (source.includes('baccarat')) return 'baccarat';
  if (source.includes('poker')) return 'poker';
  if (source.includes('dice')) return 'table';
  return 'slot';
};

api.interceptors.request.use(
  (config) => {
    startLoadingForConfig(config);
    const token = localStorage.getItem(STORAGE_KEYS.token);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    finishLoadingForConfig(response?.config || {});
    return response;
  },
  (error) => {
    finishLoadingForConfig(error?.config || {});
    if (error?.response?.status === 401) {
      localStorage.removeItem(STORAGE_KEYS.token);
      localStorage.removeItem(STORAGE_KEYS.player);

      import('@/stores/auth').then(({ useAuthStore }) => {
        const authStore = useAuthStore();
        if (authStore.isAuthenticated) {
          authStore.$patch({ token: '', player: null });
          import('@/stores/notifications').then(({ useNotificationStore }) => {
            useNotificationStore().warning(t('session.expiredMessage'), t('session.expiredTitle'));
          });
          window.location.href = '/';
        }
      });
    }
    return Promise.reject(error);
  },
);

export const API_CONFIG = {
  baseUrl: APP_CONFIG.apiBaseUrl,
  operator: APP_CONFIG.operator,
  pamRoutePrefix: APP_CONFIG.pamRoutePrefix,
  gamePlaceholderImage: APP_CONFIG.gamePlaceholderImage,
};

export const authAPI = {
  async register({ username, password, email, name, country, language, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.post(pamPath('/auth/register'), {
        operator,
        username,
        password,
        email,
        ...(name ? { name } : {}),
        ...(country ? { country } : {}),
        ...(language ? { language } : {}),
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.registerFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.registerFailed')));
    }
  },

  async verifyOtp({ otpToken, code, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.post(pamPath('/auth/otp/verify'), {
        operator,
        otp_token: otpToken,
        code,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.otpVerificationFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.otpVerificationFailed')));
    }
  },

  async login({ username, password, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.post(pamPath('/auth/login'), {
        operator,
        username,
        password,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.loginFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loginFailed')));
    }
  },

  async session({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.get(pamPath('/auth/session'), {
        params: { operator },
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.sessionValidationFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.sessionValidationFailed')));
    }
  },

  async balance({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.get(pamPath('/auth/balance'), {
        params: { operator },
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.balanceCheckFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.balanceCheckFailed')));
    }
  },

  async logout({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.post(pamPath('/auth/logout'), {
        operator,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.logoutFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.logoutFailed')));
    }
  },

  async requestPasswordRecovery({ email, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.post(pamPath('/auth/password/recover'), {
        operator,
        email,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.passwordRecoveryFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.passwordRecoveryFailed')));
    }
  },

  async resetPassword({ token, password, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.post(pamPath('/auth/password/reset'), {
        operator,
        token,
        password,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.passwordResetFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.passwordResetFailed')));
    }
  },
};

export const bonusAPI = {
  async listBonuses({ status, type, limit = 100, skip = 0 } = {}) {
    try {
      const params = {
        limit,
        skip,
      };

      if (status) {
        params.status = status;
      }
      if (type) {
        params.type = type;
      }

      const response = await api.get(pamPath('/bonus'), { params });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.bonusListFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.bonusListFailed')));
    }
  },

  async acceptBonus({ bonusId, txid }) {
    try {
      const normalizedBonusId = String(bonusId || '').trim();
      if (!normalizedBonusId) {
        throw buildError({
          message: t('apiFallback.bonusIdIsRequired'),
          code: -1,
          codeString: 'BONUS_ID_REQUIRED',
          status: 400,
        });
      }

      const response = await api.post(pamPath(`/bonus/${encodeURIComponent(normalizedBonusId)}/accept`), {
        txid: txid || `accept-${normalizedBonusId}-${Date.now()}`,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.bonusAcceptFailed')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.bonusAcceptFailed')));
    }
  },
};

export const cmsAPI = {
  async getAnnouncements({ operator = APP_CONFIG.operator, page = 1, limit = 10 } = {}) {
    try {
      const response = await api.get('/cms/announcements', {
        params: { operator, page, limit },
      });

      const data = Array.isArray(response.data) ? response.data : [];
      return { success: true, data };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadAnnouncements')));
    }
  },

  async getCarousels({
    operator = APP_CONFIG.operator,
    carouselCodes = [],
    locale = 'es',
    offset = 0,
    limit = 10,
  } = {}) {
    try {
      const codes = Array.isArray(carouselCodes) ? carouselCodes.filter(Boolean) : [];
      const response = await api.get('/cms/carousels', {
        params: {
          operator,
          carouselCodes: codes.join(','),
          locale,
          offset,
          limit,
        },
      });

      const data = Array.isArray(response.data) ? response.data : [];
      return { success: true, data };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadCarousels')));
    }
  },

  async getLobby({
    operator = APP_CONFIG.operator,
    lobbyCode,
    page = 1,
    limit = 24,
    locale = 'es',
  } = {}) {
    try {
      const response = await api.get('/cms/lobbies', {
        params: {
          operator,
          lobbyCode,
          page,
          limit,
          locale,
        },
      });

      if (!response.data || typeof response.data !== 'object') {
        throw buildError({
          message: t('apiFallback.invalidLobbyResponseFormat'),
          code: -1,
          codeString: 'INVALID_RESPONSE_FORMAT',
          status: 502,
          payload: response.data,
        });
      }

      return { success: true, data: response.data };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadLobby')));
    }
  },
};

export const casinoAPI = {
  async getProviders({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.get('/emara/providers', {
        params: { operator },
      });

      const providers = Array.isArray(response.data) ? response.data : [];
      return {
        success: true,
        data: providers.map((provider) => ({
          id: String(provider.id || ''),
          name: String(provider.name || provider.code || 'Unknown provider'),
          code: String(provider.code || ''),
          mode: provider.mode,
          platform: provider.platform,
          img: String(provider.img || '').trim(),
        })),
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadProviders')));
    }
  },

  async getGamesByProvider({ providerId, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.get(`/emara/provider/${providerId}/games`, {
        params: { operator },
      });

      const games = Array.isArray(response.data) ? response.data : [];
      return {
        success: true,
        data: games.map((game) => ({
          name: String(game.name || ''),
          code: String(game.code || ''),
          fun: Boolean(game.fun),
          img: String(game.img || '').trim(),
        })),
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadProviderGames')));
    }
  },

  async getCatalog({ operator = APP_CONFIG.operator } = {}) {
    const providersResult = await this.getProviders({ operator });
    if (!providersResult.success) {
      return providersResult;
    }

    const providers = providersResult.data;
    const gameFetches = await Promise.all(
      providers.map(async (provider) => {
        const result = await this.getGamesByProvider({ providerId: provider.id, operator });
        return {
          provider,
          result,
        };
      }),
    );

    const games = [];
    const providerErrors = [];

    for (const entry of gameFetches) {
      if (!entry.result.success) {
        providerErrors.push({
          providerId: entry.provider.id,
          providerCode: entry.provider.code,
          message: entry.result.error?.message || t('apiFallback.loadGames'),
        });
        continue;
      }

      for (const game of entry.result.data) {
        if (!game.code) {
          continue;
        }

        const type = inferTypeFromGame(game);
        games.push({
          id: game.code,
          code: game.code,
          gameid: game.code,
          name: game.name || game.code,
          providerName: entry.provider.name,
          providerCode: entry.provider.code,
          providerId: entry.provider.id,
          provider: entry.provider.name,
          fun: Boolean(game.fun),
          type,
          featured: false,
          created_at: null,
          thumbnail: game.img || APP_CONFIG.gamePlaceholderImage,
        });
      }
    }

    return {
      success: true,
      data: {
        providers,
        games,
        providerErrors,
      },
    };
  },

  async getLauncherUrl({ gameid, mode, token, lang, operator = APP_CONFIG.operator }) {
    try {
      const payload = {
        operator,
        gameid,
        mode,
        lang: String(lang || currentLocale() || 'es').slice(0, 2),
      };

      if (mode === 'real' || mode === 'social') {
        payload.token = token;
      }

      const response = await api.post('/casino/get_launcher_url_axion', payload);
      const body = response.data;

      if (!body || Number(body.error) !== 0 || !body.result?.url) {
        throw buildError({
          message: normalizeMessage(body, t('apiFallback.getLauncherUrl')),
          code: Number.isFinite(Number(body?.error)) ? Number(body.error) : -1,
          codeString: body?.code_string || '',
          status: Number(response.status || 502),
          payload: body,
        });
      }

      return {
        success: true,
        data: {
          url: String(body.result.url),
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.getLauncherUrl')));
    }
  },
};

export const sportsbookAPI = {
  async getBetbyToken({
    operator = APP_CONFIG.operator,
    accessToken = localStorage.getItem(STORAGE_KEYS.token) || '',
    lang = 'es',
  } = {}) {
    try {
      const response = await api.post(
        '/sportsbook/betby/token',
        {
          token: String(accessToken || '').trim() || null,
          lang: String(lang || 'es').slice(0, 2),
        },
        {
          params: { operator },
        },
      );

      const body = response.data;
      if (!body || typeof body !== 'object' || !Object.prototype.hasOwnProperty.call(body, 'token')) {
        throw buildError({
          message: t('apiFallback.invalidFormat', { action: t('apiFallback.getBetbyToken') }),
          code: -1,
          codeString: 'INVALID_RESPONSE_FORMAT',
          status: Number(response.status || 502),
          payload: body,
        });
      }

      return {
        success: true,
        data: {
          token: body.token || null,
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.getBetbyToken')));
    }
  },
};

export const profileAPI = {
  async getProfile({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.get(pamPath('/profile'), {
        params: { operator },
      });
      const data = ensurePamEnvelope(response.data, t('apiFallback.loadProfile'));
      return {
        success: true,
        data: {
          username: String(data.username || ''),
          name: String(data.name || ''),
          email: String(data.email || ''),
          phone: String(data.phone || ''),
          language: String(data.language || 'es'),
          country: String(data.country || 'AR'),
          profile: data.profile || {},
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadProfile')));
    }
  },

  async updateProfile({ name, phone, language, country, operator = APP_CONFIG.operator }) {
    try {
      const payload = { operator };
      if (name !== undefined) payload.name = name;
      if (phone !== undefined) payload.phone = phone;
      if (language !== undefined) payload.language = language;
      if (country !== undefined) payload.country = country;

      const response = await api.patch(pamPath('/profile'), payload);
      const data = ensurePamEnvelope(response.data, t('apiFallback.updateProfile'));
      return {
        success: true,
        data: {
          username: String(data.username || ''),
          name: String(data.name || ''),
          email: String(data.email || ''),
          phone: String(data.phone || ''),
          language: String(data.language || 'es'),
          country: String(data.country || 'AR'),
          profile: data.profile || {},
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.updateProfile')));
    }
  },

  async changePassword({ currentPassword, newPassword, operator = APP_CONFIG.operator }) {
    try {
      const response = await api.post(pamPath('/profile/password/change'), {
        operator,
        currentPassword,
        newPassword,
      });
      const data = ensurePamEnvelope(response.data, t('apiFallback.changePassword'));
      return {
        success: true,
        data: {
          passwordUpdated: Boolean(data.passwordUpdated),
          loggedOut: Boolean(data.loggedOut),
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.changePassword')));
    }
  },
};

const normalizeApiDate = (dateStr) => {
  if (!dateStr) return null;
  if (typeof dateStr === 'string' && dateStr.length === 8 && /^\d{8}$/.test(dateStr)) {
    const year = dateStr.substring(0, 4);
    const month = dateStr.substring(4, 6);
    const day = dateStr.substring(6, 8);
    return `${year}-${month}-${day}T00:00:00.000Z`;
  }
  return dateStr;
};


const isBonusLedgerEvent = (eventType) => {
  const normalized = String(eventType || '').trim().toUpperCase();
  return normalized.startsWith('BONUS_') || normalized.startsWith('PROMO_AWARD_');
};

const humanizeEventType = (eventType) => {
  const normalized = String(eventType || '').trim().toUpperCase();
  if (normalized && i18n.global.te(`transactions.events.${normalized}`)) return t(`transactions.events.${normalized}`);
  return normalized
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
};

export const transactionsAPI = {
  async getTransactions({
    page = 1,
    limit = 20,
    from,
    to,
    type,
    operator = APP_CONFIG.operator,
  } = {}) {
    try {
      const params = {
        operator,
        page,
        limit,
      };

      if (from) params.from = from;
      if (to) params.to = to;
      if (type) params.type = type;

      const response = await api.get(pamPath('/transactions'), { params });
      const data = ensurePamEnvelope(response.data, t('apiFallback.loadTransactions'));

      const rawItems = Array.isArray(data.items) ? data.items.map((item) => {
        const eventType = String(item.eventType || '');
        const details = item.details && typeof item.details === 'object' ? item.details : {};
        const isBonusEvent = isBonusLedgerEvent(eventType);

        return {
          id: String(item.id || ''),
          type: isBonusEvent ? 'bonus' : String(item.type || ''),
          eventType,
          eventLabel: humanizeEventType(eventType),
          accountType: String(item.accountType || ''),
          amount: Number(item.amount || 0),
          currency: String(item.currency || 'ARS'),
          providerType: String(item.providerType || ''),
          providerId: String(item.providerId || ''),
          txid: String(item.txid || ''),
          refType: String(item.refType || ''),
          refId: String(item.refId || ''),
          occurredAt: normalizeApiDate(item.occurredAt),
          details: {
            ...details,
            provider: isBonusEvent ? t('transactions.bonusSystem') : details.provider,
            game: isBonusEvent ? null : details.game,
            paymentMethod: isBonusEvent ? null : details.paymentMethod,
          },
        };
      }) : [];

      // Collapse ledger entries with the same txid into a single row
      const items = [];
      const txidGroups = new Map();
      for (const item of rawItems) {
        const txid = String(item.txid || '');
        if (!txid || txid === 'undefined') {
          items.push(item);
          continue;
        }
        if (!txidGroups.has(txid)) {
          txidGroups.set(txid, []);
        }
        txidGroups.get(txid).push(item);
      }
      for (const [txid, group] of txidGroups) {
        if (group.length === 1) {
          items.push(group[0]);
          continue;
        }
        // Merge entries with the same txid
        const totalAmount = group.reduce((sum, item) => sum + Number(item.amount || 0), 0);
        const betEntries = group.filter((item) => item.type === 'bet');
        const winEntries = group.filter((item) => item.type === 'win');
        const bonusEntries = group.filter((item) => item.type === 'bonus');
        // Determine the primary type: if there are bet entries, show as bet; otherwise win
        let primaryType, primaryEventType, primaryEventLabel;
        if (betEntries.length > 0) {
          primaryType = 'bet';
          primaryEventType = 'CASINO_BET';
          primaryEventLabel = t('transactions.events.CASINO_BET');
        } else if (winEntries.length > 0) {
          primaryType = 'win';
          primaryEventType = 'CASINO_WIN';
          primaryEventLabel = t('transactions.events.CASINO_WIN');
        } else {
          primaryType = group[0].type;
          primaryEventType = group[0].eventType;
          primaryEventLabel = group[0].eventLabel;
        }
        // Use the first entry as the base, but skip BONUS_DISCOUNT_APPLIED entries
        const baseEntry = group.find((item) => item.type !== 'bonus') || group[0];
        items.push({
          ...baseEntry,
          amount: totalAmount,
          type: primaryType,
          eventType: primaryEventType,
          eventLabel: primaryEventLabel,
          accountType: group.length > 1 ? 'MIXED' : baseEntry.accountType,
          details: {
            ...baseEntry.details,
            discountApplied: bonusEntries.reduce((sum, item) => sum + Number(item.amount || 0), 0) || undefined,
          },
        });
      }
      // Sort by occurredAt descending (maintain original order from API)
      items.sort((a, b) => new Date(b.occurredAt || 0) - new Date(a.occurredAt || 0));

      return {
        success: true,
        data: {
          items,
          page: Number(data.page || page),
          limit: Number(data.limit || limit),
          total: Number(data.total || 0),
          totalPages: Number(data.totalPages || 0),
          hasMore: Boolean(data.hasMore),
          filters: data.filters || { type: null, from: null, to: null },
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadTransactions')));
    }
  },
};

export const walletAPI = {
  async getProviders({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.get(pamPath('/wallet/providers'), {
        params: { operator },
      });
      const data = ensurePamEnvelope(response.data, t('apiFallback.loadWalletProviders'));
      return {
        success: true,
        data: Array.isArray(data.providers) ? data.providers : [],
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadWalletProviders')));
    }
  },

  async getBalance({ operator = APP_CONFIG.operator } = {}) {
    try {
      const response = await api.get(pamPath('/wallet/balance'), {
        params: { operator },
      });
      const data = ensurePamEnvelope(response.data, t('apiFallback.loadWalletBalance'));
      return { success: true, data };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadWalletBalance')));
    }
  },

  async getPayments({
    operator = APP_CONFIG.operator,
    page = 1,
    limit = 20,
    type,
    status,
  } = {}) {
    try {
      const response = await api.get(pamPath('/wallet/payments'), {
        params: {
          operator,
          page,
          limit,
          ...(type ? { type } : {}),
          ...(status ? { status } : {}),
        },
      });
      const data = ensurePamEnvelope(response.data, t('apiFallback.loadWalletPayments'));

      if (Array.isArray(data)) {
        return {
          success: true,
          data: {
            items: data,
            page: 1,
            limit: data.length,
            total: data.length,
            totalPages: data.length > 0 ? 1 : 0,
            hasMore: false,
          },
        };
      }

      return {
        success: true,
        data: {
          items: Array.isArray(data?.items) ? data.items : [],
          page: Number(data?.page || page),
          limit: Number(data?.limit || limit),
          total: Number(data?.total || 0),
          totalPages: Number(data?.totalPages || 0),
          hasMore: Boolean(data?.hasMore),
          filters: data?.filters || { type: null, status: null },
        },
      };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadWalletPayments')));
    }
  },

  async depositCreate({ operator = APP_CONFIG.operator, ...payload }) {
    try {
      const response = await api.post(pamPath('/wallet/deposit/create'), {
        operator,
        ...payload,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.createDeposit')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.createDeposit')));
    }
  },

  async depositExternalCreate({ operator = APP_CONFIG.operator, ...payload }) {
    try {
      const response = await api.post(pamPath('/wallet/deposit/external/create'), {
        operator,
        ...payload,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.createExternalDeposit')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.createExternalDeposit')));
    }
  },

  async withdrawCreate({ operator = APP_CONFIG.operator, ...payload }) {
    try {
      const response = await api.post(pamPath('/wallet/withdraw/create'), {
        operator,
        ...payload,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.createWithdrawal')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.createWithdrawal')));
    }
  },

  async withdrawCancel({ operator = APP_CONFIG.operator, withdrawId, reason } = {}) {
    try {
      const normalizedWithdrawId = String(withdrawId || '').trim();
      if (!normalizedWithdrawId) {
        throw buildError({
          message: t('apiFallback.withdrawIdIsRequired'),
          code: -1,
          codeString: 'WITHDRAW_ID_REQUIRED',
          status: 400,
        });
      }

      const response = await api.post(pamPath(`/wallet/withdraw/${encodeURIComponent(normalizedWithdrawId)}/cancel`), {
        operator,
        reason,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.cancelWithdrawal')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.cancelWithdrawal')));
    }
  },

  async withdrawExternalCreate({ operator = APP_CONFIG.operator, ...payload }) {
    try {
      const response = await api.post(pamPath('/wallet/withdraw/external/create'), {
        operator,
        ...payload,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.createExternalWithdrawal')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.createExternalWithdrawal')));
    }
  },

  async cryptoCurrencies({ operator = APP_CONFIG.operator, paymentId }) {
    try {
      const response = await api.post(pamPath('/wallet/crypto/currencies'), {
        operator,
        paymentId,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.loadCryptoCurrencies')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.loadCryptoCurrencies')));
    }
  },

  async cryptoCheckLimit({ operator = APP_CONFIG.operator, ...payload }) {
    try {
      const response = await api.post(pamPath('/wallet/crypto/check-limit'), {
        operator,
        ...payload,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.validateCryptoLimits')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.validateCryptoLimits')));
    }
  },

  async cryptoAddress({ operator = APP_CONFIG.operator, ...payload }) {
    try {
      const response = await api.post(pamPath('/wallet/crypto/address'), {
        operator,
        ...payload,
      });
      return { success: true, data: ensurePamEnvelope(response.data, t('apiFallback.obtainCryptoAddress')) };
    } catch (error) {
      return toPublicError(handleAxiosError(error, t('apiFallback.obtainCryptoAddress')));
    }
  },
};

const notImplementedResult = (name) => async () => {
  throw buildError({
    message: `${name} is not implemented in casino-demo`,
    code: -1,
    status: 501,
    payload: null,
  });
};

export const settingsAPI = {
  getPublicSettings: notImplementedResult('settingsAPI.getPublicSettings'),
  getUserSettings: notImplementedResult('settingsAPI.getUserSettings'),
  updateSetting: notImplementedResult('settingsAPI.updateSetting'),
};

export const containersAPI = {
  getContainers: notImplementedResult('containersAPI.getContainers'),
};

export const fragmentsAPI = {
  getFragments: notImplementedResult('fragmentsAPI.getFragments'),
};

export default api;
